import React, { useState, useMemo } from 'react';
import { useApp, UNITS, convertUnit } from '../contexts/AppContext';
import { ChefHat, Plus, Trash2, X, Check, AlertTriangle, Download, Printer } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { exportRecipesCSV, printRecipesReport } from '../utils/exportUtils';

const formatCurrency = (val) => `$${(val || 0).toFixed(2)}`;
const formatPercent = (val) => `${(val || 0).toFixed(1)}%`;

const RECIPE_CATEGORIES = ['Pizza', 'Pasta', 'Salad', 'Sandwich', 'Appetizer', 'Dessert', 'Beverage', 'Side', 'Other'];

const EMPTY_RECIPE = {
  name: '', category: 'Pizza', menuPrice: 0, targetFoodCostPercent: 30,
  targetPrimeCostPercent: 60, packagingCost: 0, prepMinutes: 0,
  cookMinutes: 0, laborWage: 15, laborBurdenPercent: 25, notes: '',
  recipeIngredients: [],
};

export default function RecipeBuilder() {
  const { ingredients, recipes, addRecipe, updateRecipe, deleteRecipe, calculateYield } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_RECIPE });

  // Calculate costs for current form
  const calculations = useMemo(() => {
    let ingredientCost = 0;
    for (const ri of form.recipeIngredients) {
      const ing = ingredients.find(i => i.id === ri.ingredientId);
      if (!ing) continue;
      const converted = convertUnit(Number(ri.amount) || 0, ri.unit, ing.purchaseUnit);
      ingredientCost += converted * ing.costPerUsableUnit;
    }

    const packagingCost = Number(form.packagingCost) || 0;
    const totalMinutes = (Number(form.prepMinutes) || 0) + (Number(form.cookMinutes) || 0);
    const loadedRate = (Number(form.laborWage) || 0) * (1 + (Number(form.laborBurdenPercent) || 0) / 100);
    const laborCost = loadedRate * totalMinutes / 60;

    const totalCost = ingredientCost + packagingCost + laborCost;
    const menuPrice = Number(form.menuPrice) || 0;
    const grossProfit = menuPrice - totalCost;
    const grossMargin = menuPrice > 0 ? (grossProfit / menuPrice) * 100 : 0;
    const foodCostPercent = menuPrice > 0 ? (ingredientCost / menuPrice) * 100 : 0;
    const primeCostPercent = menuPrice > 0 ? ((ingredientCost + laborCost) / menuPrice) * 100 : 0;

    // Suggested MSRPs
    const msrp20 = ingredientCost > 0 ? ingredientCost / 0.20 : 0;
    const msrp25 = ingredientCost > 0 ? ingredientCost / 0.25 : 0;
    const msrp30 = ingredientCost > 0 ? ingredientCost / 0.30 : 0;
    const msrp35 = ingredientCost > 0 ? ingredientCost / 0.35 : 0;

    const targetPrime = Number(form.targetPrimeCostPercent) || 60;
    const msrpPrime = (ingredientCost + laborCost) > 0 ? (ingredientCost + laborCost) / (targetPrime / 100) : 0;

    // Yield calc
    const yieldData = calculateYield(form.recipeIngredients);

    return {
      ingredientCost, packagingCost, laborCost, totalCost,
      grossProfit, grossMargin, foodCostPercent, primeCostPercent,
      msrp20, msrp25, msrp30, msrp35, msrpPrime,
      yieldData, totalMinutes, loadedRate,
    };
  }, [form, ingredients, calculateYield]);

  const addRecipeIngredient = () => {
    if (ingredients.length === 0) return;
    setForm(f => ({
      ...f,
      recipeIngredients: [...f.recipeIngredients, {
        ingredientId: ingredients[0].id,
        amount: 1,
        unit: ingredients[0].purchaseUnit,
      }],
    }));
  };

  const updateRecipeIngredient = (index, field, value) => {
    setForm(f => {
      const updated = [...f.recipeIngredients];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'ingredientId') {
        const ing = ingredients.find(i => i.id === value);
        if (ing) updated[index].unit = ing.purchaseUnit;
      }
      return { ...f, recipeIngredients: updated };
    });
  };

  const removeRecipeIngredient = (index) => {
    setForm(f => ({
      ...f,
      recipeIngredients: f.recipeIngredients.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    const data = {
      ...form,
      menuPrice: Number(form.menuPrice) || 0,
      packagingCost: Number(form.packagingCost) || 0,
      prepMinutes: Number(form.prepMinutes) || 0,
      cookMinutes: Number(form.cookMinutes) || 0,
      laborWage: Number(form.laborWage) || 0,
      laborBurdenPercent: Number(form.laborBurdenPercent) || 0,
      targetFoodCostPercent: Number(form.targetFoodCostPercent) || 30,
      targetPrimeCostPercent: Number(form.targetPrimeCostPercent) || 60,
      // Calculated fields
      ingredientCost: calculations.ingredientCost,
      totalCost: calculations.totalCost,
      grossProfit: calculations.grossProfit,
      grossMargin: calculations.grossMargin,
      foodCostPercent: calculations.foodCostPercent,
      primeCostPercent: calculations.primeCostPercent,
      laborCost: calculations.laborCost,
    };

    if (editId) {
      updateRecipe(editId, data);
      setEditId(null);
    } else {
      addRecipe(data);
    }
    setForm({ ...EMPTY_RECIPE });
    setShowForm(false);
  };

  const startEdit = (recipe) => {
    setForm({ ...recipe });
    setEditId(recipe.id);
    setShowForm(true);
  };

  return (
    <div className="page-enter p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FFC107] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
            <ChefHat className="w-5 h-5 text-[#2A2A2A]" />
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="recipes-title">
              Recipe Builder
            </h1>
            <p className="text-sm text-[#2A2A2A]/60">{recipes.length} recipes</p>
          </div>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditId(null); setForm({ ...EMPTY_RECIPE }); }}
          className="pizza-btn text-sm flex items-center gap-2"
          data-testid="add-recipe-btn"
        >
          <Plus className="w-4 h-4" /> New Recipe
        </button>
      </div>

      {/* Export Bar */}
      {recipes.length > 0 && !showForm && (
        <div className="flex gap-2 mb-4" data-testid="recipes-export-bar">
          <button onClick={() => exportRecipesCSV(recipes)} className="pizza-btn-secondary text-xs px-4 py-1.5 flex items-center gap-1" data-testid="recipes-export-csv-btn">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button onClick={() => printRecipesReport(recipes)} className="pizza-btn text-xs px-4 py-1.5 flex items-center gap-1" data-testid="recipes-print-btn">
            <Printer className="w-3.5 h-3.5" /> Print / PDF
          </button>
        </div>
      )}

      {/* Recipe Form */}
      {showForm && (
        <div className="pizza-card p-6 mb-6" data-testid="recipe-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>
              {editId ? 'Edit Recipe' : 'New Recipe'}
            </h2>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="text-[#2A2A2A]/50 hover:text-[#E23D28]">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Basic info */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Product Name *</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="pizza-input w-full" required data-testid="recipe-name-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Category</label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="border-2 border-[#2A2A2A] rounded-lg w-full" data-testid="recipe-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECIPE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Menu Price ($)</label>
                <input type="number" step="0.01" min="0" value={form.menuPrice}
                  onChange={e => setForm(f => ({ ...f, menuPrice: e.target.value }))}
                  className="pizza-input w-full" data-testid="recipe-price-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Packaging Cost ($)</label>
                <input type="number" step="0.01" min="0" value={form.packagingCost}
                  onChange={e => setForm(f => ({ ...f, packagingCost: e.target.value }))}
                  className="pizza-input w-full" data-testid="recipe-packaging-input" />
              </div>
            </div>

            {/* Targets */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Target Food Cost %</label>
                <input type="number" step="1" min="1" max="100" value={form.targetFoodCostPercent}
                  onChange={e => setForm(f => ({ ...f, targetFoodCostPercent: e.target.value }))}
                  className="pizza-input w-full" data-testid="recipe-target-food-cost-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Target Prime Cost %</label>
                <input type="number" step="1" min="1" max="100" value={form.targetPrimeCostPercent}
                  onChange={e => setForm(f => ({ ...f, targetPrimeCostPercent: e.target.value }))}
                  className="pizza-input w-full" data-testid="recipe-target-prime-cost-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Prep Minutes</label>
                <input type="number" step="1" min="0" value={form.prepMinutes}
                  onChange={e => setForm(f => ({ ...f, prepMinutes: e.target.value }))}
                  className="pizza-input w-full" data-testid="recipe-prep-minutes-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Cook/Handling Minutes</label>
                <input type="number" step="1" min="0" value={form.cookMinutes}
                  onChange={e => setForm(f => ({ ...f, cookMinutes: e.target.value }))}
                  className="pizza-input w-full" data-testid="recipe-cook-minutes-input" />
              </div>
            </div>

            {/* Labor */}
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Hourly Wage ($)</label>
                <input type="number" step="0.01" min="0" value={form.laborWage}
                  onChange={e => setForm(f => ({ ...f, laborWage: e.target.value }))}
                  className="pizza-input w-full" data-testid="recipe-wage-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Labor Burden %</label>
                <input type="number" step="1" min="0" max="100" value={form.laborBurdenPercent}
                  onChange={e => setForm(f => ({ ...f, laborBurdenPercent: e.target.value }))}
                  className="pizza-input w-full" data-testid="recipe-labor-burden-input" />
              </div>
            </div>

            {/* Ingredients */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>Ingredients Used</h3>
                <button type="button" onClick={addRecipeIngredient}
                  className="pizza-btn-secondary text-xs px-4 py-1.5 flex items-center gap-1"
                  disabled={ingredients.length === 0}
                  data-testid="add-recipe-ingredient-btn">
                  <Plus className="w-3 h-3" /> Add Ingredient
                </button>
              </div>
              {ingredients.length === 0 && (
                <p className="text-sm text-[#E23D28] flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Add ingredients to your database first.
                </p>
              )}
              {form.recipeIngredients.map((ri, idx) => (
                <div key={idx} className="flex flex-wrap items-end gap-3 mb-2 p-3 bg-[#FFF8E7] rounded-lg border border-[#2A2A2A]/10">
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs text-[#2A2A2A]/60 mb-1 block">Ingredient</label>
                    <Select value={ri.ingredientId} onValueChange={v => updateRecipeIngredient(idx, 'ingredientId', v)}>
                      <SelectTrigger className="border-2 border-[#2A2A2A] rounded-lg w-full" data-testid={`recipe-ing-select-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ingredients.map(ing => <SelectItem key={ing.id} value={ing.id}>{ing.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <label className="text-xs text-[#2A2A2A]/60 mb-1 block">Amount</label>
                    <input type="number" step="0.01" min="0" value={ri.amount}
                      onChange={e => updateRecipeIngredient(idx, 'amount', e.target.value)}
                      className="pizza-input w-full" data-testid={`recipe-ing-amount-${idx}`} />
                  </div>
                  <div className="w-28">
                    <label className="text-xs text-[#2A2A2A]/60 mb-1 block">Unit</label>
                    <Select value={ri.unit} onValueChange={v => updateRecipeIngredient(idx, 'unit', v)}>
                      <SelectTrigger className="border-2 border-[#2A2A2A] rounded-lg w-full" data-testid={`recipe-ing-unit-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <button type="button" onClick={() => removeRecipeIngredient(idx)}
                    className="p-2 hover:bg-red-100 rounded" data-testid={`remove-recipe-ing-${idx}`}>
                    <Trash2 className="w-4 h-4 text-[#E23D28]" />
                  </button>
                </div>
              ))}
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-1 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Notes</label>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="pizza-input w-full" data-testid="recipe-notes-input" />
            </div>

            {/* Live Cost Summary */}
            <div className="bg-[#2A2A2A] rounded-xl p-6 mb-6 text-white" data-testid="recipe-cost-summary">
              <h3 className="text-2xl mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>Cost Breakdown</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Ingredient Cost</p>
                  <p className="text-2xl font-bold text-[#FFC107]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(calculations.ingredientCost)}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Packaging</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(calculations.packagingCost)}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Labor Cost</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(calculations.laborCost)}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Total Cost</p>
                  <p className="text-2xl font-bold text-[#E23D28]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(calculations.totalCost)}</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Gross Profit</p>
                  <p className={`text-2xl font-bold ${calculations.grossProfit >= 0 ? 'text-[#4CAF50]' : 'text-[#E23D28]'}`} style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(calculations.grossProfit)}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Gross Margin</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatPercent(calculations.grossMargin)}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Food Cost %</p>
                  <p className={`text-2xl font-bold ${calculations.foodCostPercent > 35 ? 'text-[#E23D28]' : 'text-[#4CAF50]'}`} style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatPercent(calculations.foodCostPercent)}</p>
                </div>
                <div>
                  <p className="text-xs text-white/50 uppercase" style={{ fontFamily: "'Bebas Neue', cursive" }}>Prime Cost %</p>
                  <p className={`text-2xl font-bold ${calculations.primeCostPercent > 65 ? 'text-[#E23D28]' : 'text-[#4CAF50]'}`} style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatPercent(calculations.primeCostPercent)}</p>
                </div>
              </div>

              {/* Suggested MSRPs */}
              <div className="border-t border-white/10 pt-4 mb-4">
                <h4 className="text-lg mb-2" style={{ fontFamily: "'Bebas Neue', cursive" }}>Suggested MSRP</h4>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  {[
                    { label: 'At 20% FC', val: calculations.msrp20 },
                    { label: 'At 25% FC', val: calculations.msrp25 },
                    { label: 'At 30% FC', val: calculations.msrp30 },
                    { label: 'At 35% FC', val: calculations.msrp35 },
                    { label: `At ${form.targetPrimeCostPercent}% Prime`, val: calculations.msrpPrime },
                  ].map(({ label, val }) => (
                    <div key={label} className="bg-white/10 rounded-lg p-2 text-center">
                      <p className="text-xs text-white/50">{label}</p>
                      <p className="text-lg font-bold text-[#FFC107]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{formatCurrency(val)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Yield */}
              {calculations.yieldData.maxUnits > 0 && (
                <div className="bg-[#2E7D32] rounded-lg p-3 text-white">
                  <p className="text-sm">
                    You can make <strong className="text-lg">{calculations.yieldData.maxUnits}</strong> units from current inventory.
                    {calculations.yieldData.bottleneck && <span> Bottleneck: <strong>{calculations.yieldData.bottleneck}</strong></span>}
                  </p>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-3">
              <button type="submit" className="pizza-btn text-sm flex items-center gap-1" data-testid="recipe-submit-btn">
                <Check className="w-4 h-4" /> {editId ? 'Update Recipe' : 'Save Recipe'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); }}
                className="pizza-btn-secondary text-sm" data-testid="recipe-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Recipe List */}
      {recipes.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recipes-list">
          {recipes.map(recipe => (
            <div key={recipe.id} className="pizza-card p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }}>{recipe.name}</h3>
                  <span className="pizza-tag pizza-tag-yellow text-xs">{recipe.category}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(recipe)} className="p-1.5 hover:bg-[#FFC107]/20 rounded" data-testid={`edit-recipe-${recipe.id}`}>
                    <ChefHat className="w-4 h-4 text-[#2A2A2A]" />
                  </button>
                  <button onClick={() => deleteRecipe(recipe.id)} className="p-1.5 hover:bg-red-100 rounded" data-testid={`delete-recipe-${recipe.id}`}>
                    <Trash2 className="w-4 h-4 text-[#E23D28]" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[#2A2A2A]/50 text-xs">Menu Price</p>
                  <p className="font-bold text-[#2A2A2A]">{formatCurrency(recipe.menuPrice)}</p>
                </div>
                <div>
                  <p className="text-[#2A2A2A]/50 text-xs">Total Cost</p>
                  <p className="font-bold text-[#E23D28]">{formatCurrency(recipe.totalCost)}</p>
                </div>
                <div>
                  <p className="text-[#2A2A2A]/50 text-xs">Gross Profit</p>
                  <p className={`font-bold ${recipe.grossProfit >= 0 ? 'text-[#2E7D32]' : 'text-[#E23D28]'}`}>{formatCurrency(recipe.grossProfit)}</p>
                </div>
                <div>
                  <p className="text-[#2A2A2A]/50 text-xs">Food Cost %</p>
                  <p className={`font-bold ${recipe.foodCostPercent > 35 ? 'text-[#E23D28]' : 'text-[#2E7D32]'}`}>{formatPercent(recipe.foodCostPercent)}</p>
                </div>
              </div>
              {recipe.recipeIngredients && recipe.recipeIngredients.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#F0E6D2]">
                  <p className="text-xs text-[#2A2A2A]/40">{recipe.recipeIngredients.length} ingredient(s)</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : !showForm && (
        <div className="pizza-card p-8 text-center" data-testid="recipes-empty">
          <ChefHat className="w-12 h-12 text-[#2A2A2A]/20 mx-auto mb-3" />
          <h3 className="text-xl text-[#2A2A2A] mb-1" style={{ fontFamily: "'Bebas Neue', cursive" }}>No Recipes Yet</h3>
          <p className="text-sm text-[#2A2A2A]/50 mb-4">Build your first recipe to see real cost breakdowns.</p>
          <button onClick={() => { setShowForm(true); setEditId(null); setForm({ ...EMPTY_RECIPE }); }}
            className="pizza-btn text-sm" data-testid="empty-add-recipe-btn">
            <Plus className="w-4 h-4 inline mr-1" /> Create First Recipe
          </button>
        </div>
      )}
    </div>
  );
}
